import { ReactNode, useState } from "react";
import {
  AdjustmentsHorizontalIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
  EnvelopeIcon,
  LinkIcon,
  PencilSquareIcon,
  PlusIcon,
  PresentationChartBarIcon,
} from "@heroicons/react/24/outline";
import { IWebsite } from "../../models/Website";
import { ISendingDomain } from "../../models/SendingDomain";
import useNotification from "../notifications/useNotifications";
import useSendingDomains from "../../data/useSendingDomains";
import useCustomer from "../../data/useCustomer";
import { getEmbedCode, getWidgetUrl } from "../../utils/widgetEmbed";
import { resolveWebsiteSender, WebsiteSender } from "../../utils/senderResolution";
import Pill from "../shared/Pill";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import AddEditWebsiteModal, { FONT_OPTIONS } from "./AddEditWebsiteModal";
import WebsiteDisplaySettingsModal from "./WebsiteDisplaySettingsModal";

export const SENDING_DOMAINS_ANCHOR = "sending-domains";

interface WebsiteCardsProps {
  token: string;
  customerId: string;
  websites: IWebsite[];
}

/**
 * One full-width card per website: what it looks like, which address its quote emails come
 * from, and the embed code / URL - all visible without expanding anything.
 */
export default function WebsiteCards({ token, customerId, websites }: WebsiteCardsProps) {
  const { addNotification } = useNotification();
  const { sendingDomains } = useSendingDomains();
  const { customer } = useCustomer();

  const [editOpen, setEditOpen] = useState(false);
  const [editWebsite, setEditWebsite] = useState<IWebsite | undefined>(undefined);
  const [displayOpen, setDisplayOpen] = useState(false);
  const [displayWebsite, setDisplayWebsite] = useState<IWebsite | undefined>(undefined);

  const domains: ISendingDomain[] = sendingDomains.data?.data ?? [];
  const customerData = customer.data?.data;

  const copyToClipboard = (text: string, what: string) => {
    navigator.clipboard.writeText(text);
    addNotification({ variant: "success", primaryText: `${what} copied to clipboard` });
  };

  const openEdit = (website?: IWebsite) => {
    setEditWebsite(website);
    setEditOpen(true);
  };

  const openDisplay = (website: IWebsite) => {
    setDisplayWebsite(website);
    setDisplayOpen(true);
  };

  const sorted = [...websites].sort((a, b) => a.domain.localeCompare(b.domain));

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Websites</h2>
          <p className="text-sm text-gray-600 mt-1">
            Each website gets its own widget, branding and sending address. Copy the embed code
            into the site, or link straight to the widget URL.
          </p>
        </div>
        <ButtonInput
          isSubmit={false}
          onClick={() => openEdit(undefined)}
          classes="bg-green-600 hover:bg-green-700 inline-flex items-center gap-1.5"
        >
          <span className="inline-flex items-center gap-1.5">
            <PlusIcon className="h-4 w-4" /> Add website
          </span>
        </ButtonInput>
      </div>

      {sorted.length === 0 ? (
        <div className="mt-4 w-full rounded-lg border-2 border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-gray-700 font-medium">No websites yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Add a website to get an embed code and start taking quotes through the widget.
          </p>
          <div className="mt-4">
            <ButtonInput
              isSubmit={false}
              label="Add your first website"
              onClick={() => openEdit(undefined)}
              classes="bg-green-600 hover:bg-green-700"
            />
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          {sorted.map((website) => (
            <WebsiteCard
              key={website.id}
              website={website}
              sender={resolveWebsiteSender(website, domains, customerData)}
              embedCode={getEmbedCode(token, customerId, website)}
              widgetUrl={getWidgetUrl(token, customerId, website)}
              onCopy={copyToClipboard}
              onEdit={() => openEdit(website)}
              onDisplaySettings={() => openDisplay(website)}
            />
          ))}
        </div>
      )}

      <AddEditWebsiteModal
        open={editOpen}
        setOpen={() => {
          setEditOpen(false);
          setEditWebsite(undefined);
        }}
        website={editWebsite}
      />
      <WebsiteDisplaySettingsModal
        open={displayOpen}
        setOpen={() => {
          setDisplayOpen(false);
          setDisplayWebsite(undefined);
        }}
        website={displayWebsite}
      />
    </>
  );
}

interface WebsiteCardProps {
  website: IWebsite;
  sender: WebsiteSender;
  embedCode: string;
  widgetUrl: string;
  onCopy: (text: string, what: string) => void;
  onEdit: () => void;
  onDisplaySettings: () => void;
}

export function WebsiteCard({ website, sender, embedCode, widgetUrl, onCopy, onEdit, onDisplaySettings }: WebsiteCardProps) {
  const fontLabel = FONT_OPTIONS.find((f) => f.value === (website.fontFamily || "poppins"))?.label ?? website.fontFamily;
  const swatches = [
    { label: "Primary", value: website.primaryColor },
    { label: "Secondary", value: website.secondaryColor },
    { label: "Accent", value: website.accentColor },
  ].filter((s) => !!s.value);

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Header: identity + actions */}
      <div
        className="px-5 py-4 border-b border-gray-200 flex flex-wrap items-start justify-between gap-3"
        style={{ borderTop: `4px solid ${website.primaryColor || "#035196"}` }}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={website.domain}
              target="_blank"
              rel="noreferrer"
              className="text-lg font-semibold text-gray-900 hover:text-indigo-600 inline-flex items-center gap-1 break-all"
            >
              {prettyDomain(website.domain)}
              <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400 shrink-0" />
            </a>
            {website.googleTag && <Pill label={website.googleTag} tone="gray" />}
          </div>
          <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
            <Fact label="Resume page" value={website.resumeDomain} href={website.resumeDomain} />
            <Fact label="FAQ" value={website.faqUrl} href={website.faqUrl} />
            <Fact label="Font" value={fontLabel} />
            {swatches.length > 0 && (
              <div className="flex items-center gap-1.5">
                <dt className="sr-only">Colours</dt>
                <dd className="flex items-center gap-1">
                  {swatches.map((s) => (
                    <span
                      key={s.label}
                      title={`${s.label}: ${s.value}`}
                      className="inline-block h-4 w-4 rounded-full ring-1 ring-gray-300"
                      style={{ backgroundColor: s.value! }}
                    />
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          <ActionButton icon={<PencilSquareIcon className="h-4 w-4" />} label="Edit" onClick={onEdit} primary />
          <ActionButton icon={<AdjustmentsHorizontalIcon className="h-4 w-4" />} label="Display settings" onClick={onDisplaySettings} />
          <ActionButton
            icon={<PresentationChartBarIcon className="h-4 w-4" />}
            label="Stats"
            onClick={() => window.open(`/stats/website/${website.id}`, "_blank")}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
        {/* Sending address */}
        <section className="px-5 py-4">
          <SectionTitle icon={<EnvelopeIcon className="h-4 w-4" />} title="Quote emails sent from" />
          <SenderSummary sender={sender} onEdit={onEdit} />
        </section>

        {/* Embed */}
        <section className="px-5 py-4">
          <SectionTitle icon={<CodeBracketIcon className="h-4 w-4" />} title="Embed" />
          <div className="mt-2 flex flex-wrap gap-2">
            <ActionButton
              icon={<ClipboardDocumentIcon className="h-4 w-4" />}
              label="Copy embed code"
              onClick={() => onCopy(embedCode, "Embed code")}
              primary
            />
            <ActionButton
              icon={<LinkIcon className="h-4 w-4" />}
              label="Copy widget URL"
              onClick={() => onCopy(widgetUrl, "Widget URL")}
            />
            <ActionButton
              icon={<ArrowTopRightOnSquareIcon className="h-4 w-4" />}
              label="Open widget"
              onClick={() => window.open(widgetUrl, "_blank")}
            />
          </div>
          <button
            type="button"
            title="Click to copy"
            onClick={() => onCopy(widgetUrl, "Widget URL")}
            className="mt-3 w-full text-left font-mono text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 truncate hover:bg-gray-100"
          >
            {widgetUrl}
          </button>
          <p className="mt-2 text-xs text-gray-500">
            Paste the embed code where the widget should appear. It picks up the iframe height and
            z-index from Display settings.
          </p>
        </section>
      </div>
    </div>
  );
}

function SenderSummary({ sender, onEdit }: { sender: WebsiteSender; onEdit: () => void }) {
  const verifyLink = (
    <a href={`#${SENDING_DOMAINS_ANCHOR}`} className="text-indigo-600 hover:underline">
      verify it below
    </a>
  );

  if (sender.status === "verified") {
    return (
      <div className="mt-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm text-gray-900 break-all">{sender.from}</span>
          <Pill label="Verified" tone="green" />
        </div>
        {sender.fromName && (
          <p className="mt-1 text-xs text-gray-500">
            Shown as <span className="text-gray-700">“{sender.fromName}”</span>
          </p>
        )}
        <ChangeLink onEdit={onEdit} />
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-900">Wilma platform address</span>
        <Pill label="Default" tone="gray" />
      </div>
      {sender.replyTo && (
        <p className="mt-1 text-xs text-gray-500">
          Replies go to <span className="font-mono text-gray-700">{sender.replyTo}</span>
        </p>
      )}

      {sender.status === "pending" && (
        <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
          Set to send from <span className="font-mono">{sender.configuredFrom}</span> but{" "}
          <span className="font-medium">{sender.domain?.domain}</span> isn’t verified yet — add its DNS
          records and {verifyLink}.
        </p>
      )}
      {sender.status === "orphaned" && (
        <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5">
          Set to send from <span className="font-mono">{sender.configuredFrom}</span> but that domain is no
          longer registered on your account. Pick another domain or clear it.
        </p>
      )}
      {sender.status === "platform" && (
        <p className="mt-2 text-xs text-gray-500">
          To send from your own address, add and {verifyLink}, then attach it to this website.
        </p>
      )}
      <ChangeLink onEdit={onEdit} label={sender.status === "platform" ? "Choose sending address" : "Change"} />
    </div>
  );
}

function ChangeLink({ onEdit, label = "Change" }: { onEdit: () => void; label?: string }) {
  return (
    <button type="button" onClick={onEdit} className="mt-2 text-xs font-medium text-indigo-600 hover:underline">
      {label}
    </button>
  );
}

function SectionTitle({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {icon} {title}
    </h4>
  );
}

function Fact({ label, value, href }: { label: string; value: string | null | undefined; href?: string | null }) {
  if (!value) return null;
  const text = href ? prettyDomain(value) : value;
  return (
    <div className="flex items-center gap-1 min-w-0">
      <dt className="text-gray-400">{label}:</dt>
      <dd className="truncate max-w-[16rem]">
        {href ? (
          <a href={href} target="_blank" rel="noreferrer" className="hover:text-indigo-600 hover:underline" title={value}>
            {text}
          </a>
        ) : (
          <span title={value}>{text}</span>
        )}
      </dd>
    </div>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  primary = false,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium shadow-sm border transition-colors",
        primary
          ? "bg-indigo-600 border-transparent text-white hover:bg-indigo-700"
          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
      ].join(" ")}
    >
      {icon}
      {label}
    </button>
  );
}

/** "https://www.example.com/" -> "example.com" for display only. */
function prettyDomain(value: string) {
  try {
    const url = new URL(value.includes("://") ? value : `https://${value}`);
    const path = url.pathname === "/" ? "" : url.pathname.replace(/\/$/, "");
    return `${url.host.replace(/^www\./, "")}${path}`;
  } catch {
    return value;
  }
}
